/**
 * DateGroupedList — renders tasks grouped by date periods with virtualization.
 *
 * Used in Upcoming, Logbook, and Trash views.
 *
 * - Upcoming: groups by `startAtData.startAt` into day/month/year periods.
 *   ALL periods are rendered (including empty) to serve as future DnD targets.
 * - Logbook: groups by `task.statusAt` into history periods (today, yesterday, etc.)
 * - Trash: groups by `task.deletedAt` into history periods.
 *
 * All rows (both headers and task items) are virtualized via @tanstack/react-virtual
 * to handle large datasets (1000+ items in Logbook/Trash) without DOM bloat.
 *
 * @see docs/plans/view-and-dnd-master-plan.md §12, §34
 */

import { useMemo, memo, useCallback, useRef, useEffect } from "react"
import { useVirtualizer } from "@tanstack/react-virtual"
import { useMainScroll } from "@/app/providers/MainScrollProvider"
import type { ViewItem } from "../hooks/useViewData"
import { TaskItem } from "@/features/tasks/components/TaskItem"
import { useTaskActions } from "@/features/tasks/hooks/useTaskActions"
import { useActiveTask } from "@/features/task-detail/hooks/useActiveTask"
import { DateGroupHeader } from "./DateGroupHeader"
import type { DateGroup, DatePeriod } from "../utils/dateGrouping"
import {
  generateUpcomingPeriods,
  groupUpcomingItems,
  groupHistoryItems,
  logbookTimestamp,
  trashTimestamp,
} from "../utils/dateGrouping"
import type { EventOccurrence } from "@/features/calendar/types"
import { filterEventsForPeriod } from "@/features/calendar/icalUtils"
import { CalendarEventList } from "@/features/calendar/CalendarEventList"
import { useCalendarStore } from "@/features/calendar/calendarStore"
import { useCurrentSubscription } from "@/features/settings/hooks/useCurrentSubscription"
import { PaymentPlan } from "@/features/settings/data/subscriptionPlans"
import { useViewContext } from "@/app/stores/viewContext"
import { ObjectType, ObjectStatus } from "@/db/schema"
import { parseDbDate } from "@totodo/operations"
import { cn } from "@/shared/utils/general/cn"
import dayjs from "dayjs"

// ---------------------------------------------------------------------------
// Constants
// ---------------------------------------------------------------------------

/** Stable empty array — avoids new reference per render in Zustand selectors */
const EMPTY_EVENTS: EventOccurrence[] = []

/**
 * Task row height estimate.
 * All date-grouped views (logbook, trash, upcoming) show a ContainerLabel
 * subtitle under the title → ~60px instead of the usual 44px for flat views.
 * measureElement will refine, but a close estimate avoids layout jumps.
 */
const TASK_ROW_HEIGHT = 60
const OVERSCAN = 15
/** Active (expanded) task row estimate — matches ViewShell/DndTaskList */
const ACTIVE_ROW_HEIGHT = 220

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

type DateGroupMode = "upcoming" | "logbook" | "trash"

interface DateGroupedListProps {
  /** The items from useViewData (already sorted) */
  items: ViewItem[]
  /** Which grouping strategy to use */
  mode: DateGroupMode
}

/**
 * A flat virtual row — either a group header or a task item.
 * We flatten all groups into a single array so @tanstack/react-virtual
 * can virtualize the entire list with one virtualizer.
 */
type VirtualRow =
  | {
      type: "header"
      period: DateGroup<ViewItem>["period"]
      count: number
      isFirst: boolean
      variant: "upcoming" | "history"
    }
  | {
      type: "task"
      item: ViewItem
    }
  | {
      type: "calendar"
      events: EventOccurrence[]
      period: DatePeriod
    }
  | {
      /** Compact row grouping multiple recurring instances from the same template */
      type: "recurring-group"
      /** The first instance (used for display) */
      item: ViewItem
      /** Total instance count in this period */
      instanceCount: number
    }

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

/** Height estimate for the calendar event block per period (will be refined by measureElement) */
const CALENDAR_EVENTS_ROW_HEIGHT = 28

export const DateGroupedList = memo(function DateGroupedList({
  items,
  mode,
}: DateGroupedListProps) {
  const { completeTask, uncompleteTask } = useTaskActions()
  const { activeTaskId, isEditingTitle, openTask, closeTask } = useActiveTask()
  const { scrollRef } = useMainScroll()
  const listRef = useRef<HTMLDivElement>(null)

  // Read calendar events directly from store (upcoming mode only)
  // IMPORTANT: must return a stable reference for non-upcoming modes.
  // Returning a new `[]` literal would cause useSyncExternalStore's
  // post-commit snapshot check to always see a different reference,
  // triggering an infinite re-render loop (Zustand v5 + React 19).
  const allCalendarEvents = useCalendarStore((s) =>
    mode === "upcoming" ? s.allEvents : EMPTY_EVENTS,
  )
  const subscription = useCurrentSubscription()
  const isFreeUser =
    subscription?.currentSubscription?.plan === PaymentPlan.Free
  const calendarEvents =
    mode === "upcoming" && !isFreeUser ? allCalendarEvents : undefined

  const variant = mode === "upcoming" ? "upcoming" : "history"

  // Compute groups based on mode
  const groups = useMemo(() => {
    switch (mode) {
      case "upcoming":
        return computeUpcomingGroups(items)
      case "logbook":
        return groupHistoryItems(items, logbookTimestamp)
      case "trash":
        return groupHistoryItems(items, trashTimestamp)
    }
  }, [items, mode])

  // ── Push visibleTaskIds with date-group separators ────────────────────
  // Date groups are visually separated by headers (Sunday, Monday, etc.).
  // Insert synthetic separators so selection breaks at date boundaries,
  // just like GroupedTaskList does for container groups.
  const setVisibleTaskIds = useViewContext((s) => s.setVisibleTaskIds)
  const setVisibleSectionIds = useViewContext((s) => s.setVisibleSectionIds)
  const setGroupedAllTaskIds = useViewContext((s) => s.setGroupedAllTaskIds)
  useEffect(() => {
    const ids: string[] = []
    const allGroups: string[][] = []
    const sectionIds = new Set<string>()

    for (const group of groups) {
      const groupTaskIds: string[] = []
      for (const vi of group.items) {
        const id = vi.task.id
        if (!id) continue
        if (
          vi.task.type === ObjectType.Task ||
          vi.task.type === ObjectType.Section ||
          vi.task.type === ObjectType.Project ||
          vi.task.type === ObjectType.List
        ) {
          groupTaskIds.push(id)
          if (vi.task.type === ObjectType.Section) {
            sectionIds.add(id)
          }
        }
      }
      if (groupTaskIds.length === 0) continue
      // Insert separator before non-first groups with tasks
      if (ids.length > 0) {
        ids.push(`__group_sep__date_${group.period.key}`)
      }
      ids.push(...groupTaskIds)
      allGroups.push(groupTaskIds)
    }

    setVisibleTaskIds(ids)
    setVisibleSectionIds(sectionIds)
    setGroupedAllTaskIds(allGroups)
  }, [groups, setVisibleTaskIds, setVisibleSectionIds, setGroupedAllTaskIds])

  // Pre-compute calendar events per period (upcoming mode only)
  const calendarByPeriod = useMemo(() => {
    if (mode !== "upcoming" || !calendarEvents || calendarEvents.length === 0)
      return null
    const map = new Map<string, EventOccurrence[]>()
    for (const group of groups) {
      const p = group.period as DatePeriod
      const startDate = dayjs(p.startMs).format("YYYY-MM-DD")
      const endDate = dayjs(p.endMs).format("YYYY-MM-DD")
      const events = filterEventsForPeriod(calendarEvents, startDate, endDate)
      if (events.length > 0) map.set(p.key, events)
    }
    return map
  }, [mode, calendarEvents, groups])

  // Flatten groups into virtual rows + compute per-task group position
  const { flatRows, taskPositions } = useMemo(() => {
    const rows: VirtualRow[] = []
    // Map from flatRows index → { isFirst, isLast } for task rows
    const positions = new Map<number, { isFirst: boolean; isLast: boolean }>()

    groups.forEach((group, groupIndex) => {
      rows.push({
        type: "header",
        period: group.period,
        count: group.items.length,
        isFirst: groupIndex === 0,
        variant,
      })

      // Insert calendar events block after header (upcoming mode only)
      if (calendarByPeriod) {
        const events = calendarByPeriod.get(group.period.key)
        if (events && events.length > 0) {
          rows.push({
            type: "calendar",
            events,
            period: group.period as DatePeriod,
          })
        }
      }

      const taskStartIdx = rows.length
      // In upcoming mode, group recurring instances by recurringTemplateId
      // so "Olivolja" appearing 5× in a month shows as "Olivolja (5×)" instead.
      if (mode === "upcoming" && group.items.length > 0) {
        const grouped = collapseRecurringInstances(group.items)
        for (const entry of grouped) {
          if (entry.count > 1) {
            rows.push({
              type: "recurring-group",
              item: entry.item,
              instanceCount: entry.count,
            })
          } else {
            rows.push({ type: "task", item: entry.item })
          }
        }
      } else {
        for (const item of group.items) {
          rows.push({ type: "task", item })
        }
      }
      const taskEndIdx = rows.length - 1
      // Mark first/last task in each date group for border-radius.
      // Sections break contiguous blocks just like in other list renderers.
      for (let i = taskStartIdx; i <= taskEndIdx; i++) {
        const row = rows[i]
        if (!row || (row.type !== "task" && row.type !== "recurring-group"))
          continue
        const isSection = row.type === "task" && row.item.task.type === ObjectType.Section
        if (isSection) {
          positions.set(i, { isFirst: true, isLast: true })
          continue
        }
        // Check previous task row (skip non-task rows)
        let prevIsFirst = true
        for (let p = i - 1; p >= taskStartIdx; p--) {
          const pr = rows[p]
          if (pr?.type === "task" || pr?.type === "recurring-group") {
            prevIsFirst =
              pr.type === "task" &&
              pr.item.task.type === ObjectType.Section
            break
          }
        }
        // Check next task row
        let nextIsLast = true
        for (let n = i + 1; n <= taskEndIdx; n++) {
          const nr = rows[n]
          if (nr?.type === "task" || nr?.type === "recurring-group") {
            nextIsLast =
              nr.type === "task" &&
              nr.item.task.type === ObjectType.Section
            break
          }
        }
        positions.set(i, { isFirst: prevIsFirst, isLast: nextIsLast })
      }
    })
    return { flatRows: rows, taskPositions: positions }
  }, [groups, variant, calendarByPeriod, mode])

  const virtualizer = useVirtualizer({
    count: flatRows.length,
    getScrollElement: () => scrollRef.current,
    estimateSize: (index) => {
      const row = flatRows[index]
      if (!row) return TASK_ROW_HEIGHT
      if (row.type === "header") {
        // Header heights vary by variant and position.
        // Use generous estimates — measureElement will refine.
        if (row.variant === "history") {
          // History: pt-1 + text ~20px + pb-2.5 + border + mb-2.5 ≈ 42px
          // Non-first groups also have mt-[30px] ≈ +30px
          return row.isFirst ? 42 : 72
        }
        // Upcoming: min-h-38 + large text ~40px ≈ 60px
        // Non-first: +50px margin
        return row.isFirst ? 60 : 110
      }
      if (row.type === "calendar") {
        // Estimate: ~28px per visible event (capped at 10 by CalendarEventList)
        return Math.min(row.events.length, 10) * CALENDAR_EVENTS_ROW_HEIGHT
      }
      // Task row or recurring group row
      if (row.type === "recurring-group") return TASK_ROW_HEIGHT
      if (row.item.task.id === activeTaskId) return ACTIVE_ROW_HEIGHT
      return TASK_ROW_HEIGHT
    },
    overscan: OVERSCAN,
    scrollMargin: listRef.current?.offsetTop ?? 0,
  })

  // Re-measure all items when the row composition changes.
  // - activeTaskId: expanded task row changes height dramatically (44→220+)
  // - calendarByPeriod: async calendar data inserts new rows, shifting indices
  //   and invalidating the virtualizer's per-index measurement cache.
  useEffect(() => {
    virtualizer.measure()
  }, [activeTaskId, calendarByPeriod, virtualizer])

  const handleComplete = useCallback(
    (id: string) => completeTask(id),
    [completeTask],
  )
  const handleUncomplete = useCallback(
    (id: string) => uncompleteTask(id),
    [uncompleteTask],
  )

  return (
    <div ref={listRef}>
      <div
        className="relative w-full"
        style={{ height: virtualizer.getTotalSize() }}
      >
        {virtualizer.getVirtualItems().map((virtualRow) => {
          const row = flatRows[virtualRow.index]
          if (!row) return null

          if (row.type === "header") {
            return (
              <div
                key={`header-${row.period.key}`}
                data-index={virtualRow.index}
                ref={virtualizer.measureElement}
                className="absolute left-0 w-full"
                style={{
                  top: virtualRow.start - virtualizer.options.scrollMargin,
                }}
              >
                <VirtualDateGroupHeader
                  period={row.period}
                  count={row.count}
                  isFirst={row.isFirst}
                  variant={row.variant}
                />
              </div>
            )
          }

          if (row.type === "calendar") {
            const p = row.period
            const periodType = p.type === "day" ? "day" : "month"
            return (
              <div
                key={`cal-${p.key}`}
                data-index={virtualRow.index}
                ref={virtualizer.measureElement}
                className="absolute left-0 w-full"
                style={{
                  top: virtualRow.start - virtualizer.options.scrollMargin,
                }}
              >
                <div className="mx-3 pt-3 pb-5 desk:mx-0">
                  <CalendarEventList
                    events={row.events}
                    period={{
                      id: p.key,
                      type: periodType,
                      startDate: dayjs(p.startMs),
                      endDate: dayjs(p.endMs),
                    }}
                  />
                </div>
              </div>
            )
          }

          if (row.type === "recurring-group") {
            const taskId = row.item.task.id ?? ""
            const pos = taskPositions.get(virtualRow.index)
            return (
              <div
                key={`rg-${taskId}`}
                data-index={virtualRow.index}
                ref={virtualizer.measureElement}
                className="absolute left-0 w-full"
                style={{
                  top: virtualRow.start - virtualizer.options.scrollMargin,
                }}
              >
                <RecurringGroupRow
                  item={row.item}
                  instanceCount={row.instanceCount}
                  onComplete={handleComplete}
                  onOpen={openTask}
                  isFirstInGroup={pos?.isFirst}
                  isLastInGroup={pos?.isLast}
                />
              </div>
            )
          }

          // Task row
          const taskId = row.item.task.id ?? ""
          const isActive = taskId === activeTaskId
          const pos = taskPositions.get(virtualRow.index)
          return (
            <div
              key={taskId}
              data-index={virtualRow.index}
              ref={virtualizer.measureElement}
              className="absolute left-0 w-full"
              style={{
                top: virtualRow.start - virtualizer.options.scrollMargin,
                zIndex: isActive ? 50 : undefined,
              }}
            >
              <TaskItem
                task={row.item.task}
                onComplete={handleComplete}
                onUncomplete={handleUncomplete}
                isActive={isActive}
                startAtData={row.item.startAtData}
                editTitle={isActive && isEditingTitle}
                onOpen={openTask}
                onClose={closeTask}
                isFirstInGroup={pos?.isFirst}
                isLastInGroup={pos?.isLast}
              />
            </div>
          )
        })}
      </div>
    </div>
  )
})

// ---------------------------------------------------------------------------
// Virtual header row — wraps DateGroupHeader with correct spacing
// ---------------------------------------------------------------------------

/**
 * Renders a DateGroupHeader inside the virtual list.
 * The mt-[30px] gap between groups is applied here to be measured
 * correctly by the virtualizer.
 */
const VirtualDateGroupHeader = memo(function VirtualDateGroupHeader({
  period,
  count,
  isFirst,
  variant,
}: {
  period: DateGroup<ViewItem>["period"]
  count: number
  isFirst: boolean
  variant: "upcoming" | "history"
}) {
  const dateNumber =
    "dateNumber" in period ? (period as DatePeriod).dateNumber : undefined

  return (
    <div className={isFirst ? undefined : "mt-[50px]"}>
      <DateGroupHeader
        label={period.label}
        count={count}
        dateNumber={dateNumber}
        isFirst={isFirst}
        variant={variant}
      />
    </div>
  )
})

// ---------------------------------------------------------------------------
// Upcoming grouping helper
// ---------------------------------------------------------------------------

/**
 * Compute Upcoming groups: generate periods based on the latest startAt,
 * then assign items to those periods.
 */
function computeUpcomingGroups(items: ViewItem[]): DateGroup<ViewItem>[] {
  // Find the latest startAt to determine how many year-periods to generate
  let latestMs = 0
  for (const item of items) {
    const ts = parseDbDate(item.startAtData?.startAt) ?? 0
    if (ts > latestMs) latestMs = ts
  }

  const periods = generateUpcomingPeriods(latestMs || undefined)
  return groupUpcomingItems(items, periods)
}

// ---------------------------------------------------------------------------
// Recurring instance grouping — collapses instances sharing a template
// ---------------------------------------------------------------------------

interface CollapsedEntry {
  item: ViewItem
  count: number
}

/**
 * Within a date period's items, collapse consecutive recurring instances
 * that share the same `recurringTemplateId` into a single entry with a count.
 * Non-recurring items pass through unchanged.
 */
function collapseRecurringInstances(items: ViewItem[]): CollapsedEntry[] {
  const result: CollapsedEntry[] = []
  // Group by recurringTemplateId — instances with the same template get merged
  const templateGroups = new Map<string, { item: ViewItem; count: number }>()
  const order: (string | ViewItem)[] = [] // track insertion order

  for (const vi of items) {
    const tmplId = vi.task.recurringTemplateId as string | undefined
    if (tmplId) {
      const existing = templateGroups.get(tmplId)
      if (existing) {
        existing.count++
      } else {
        const entry = { item: vi, count: 1 }
        templateGroups.set(tmplId, entry)
        order.push(tmplId)
      }
    } else {
      order.push(vi)
    }
  }

  for (const key of order) {
    if (typeof key === "string") {
      const entry = templateGroups.get(key)!
      result.push(entry)
    } else {
      result.push({ item: key, count: 1 })
    }
  }
  return result
}

// ---------------------------------------------------------------------------
// RecurringGroupRow — compact row for grouped recurring instances
// ---------------------------------------------------------------------------

import { ContainerLabel } from "@/features/tasks/components/ContainerLabel"
import { TaskCheckbox } from "@/features/tasks/components/TaskCheckbox"
import RepeatIcon from "@/shared/icons/pikaicons/stroke/arrows-chevrons/repeat-square.svg?react"

const RecurringGroupRow = memo(function RecurringGroupRow({
  item,
  instanceCount,
  onComplete,
  onOpen,
  isFirstInGroup,
  isLastInGroup,
}: {
  item: ViewItem
  instanceCount: number
  onComplete: (id: string) => void
  onOpen: (id: string) => void
  isFirstInGroup?: boolean
  isLastInGroup?: boolean
}) {
  const task = item.task
  const taskId = task.id as string

  return (
    <div
      data-task-id={taskId}
      className={cn(
        "group flex items-center gap-2 bg-card px-3 py-2.5 cursor-pointer hover:bg-accent/30 transition-colors",
        isFirstInGroup && "rounded-t-lg",
        isLastInGroup && "rounded-b-lg",
      )}
      onClick={() => onOpen(taskId)}
    >
      <TaskCheckbox
        status={task.status as ObjectStatus}
        onToggle={() => onComplete(taskId)}
        isRecurringTemplate={0}
      />
      <div className="min-w-0 flex-1">
        <div className="flex items-center gap-1.5">
          <span className="truncate text-sm">{task.title}</span>
          <span className="inline-flex shrink-0 items-center gap-0.5 rounded-full bg-muted px-1.5 py-0.5 text-[10px] font-medium text-muted-foreground">
            <RepeatIcon className="size-3" />
            {instanceCount}×
          </span>
        </div>
        <ContainerLabel task={task} />
      </div>
    </div>
  )
})
